import { extendTheme } from "@mui/joy/styles";

// --primary-green-25: #F7F7F3;
//     --primary-green-50: #EEEEE7;
//     --primary-green-100: #D5D7D0;
//     --primary-green-200: #BCC0B9;
//     --primary-green-300: #A3A9A2;
// --bgDark: #f4f1e9;
// --bgLight: #f9f8f2;
// --bgBrand: #c9d0da;
// --bgBrandLight: #f9f9f9;
// --secondaryDark: #5e6f6e;
// --secondaryMedium: #7b8881;
// --primaryLight: #e2c8a7;
// --primaryDark: #28374d;
// --secondaryLight: #e4d7a8;
// --thirdLight: #7b8ba0;
// --fourthLight: #f2f5f6;
// --fifthLight: #e8ecef;
// --shade100: #646361;
// --shade70: #848787;
// --shade50: #aaa;
// --shade30: #dfdedb;
// --grade1back: #cef0e4;
// --grade2back: #fff597;
// --grade3back: #ffbc6d;
// --grade4back: #ff8989;
// --success: #51b076;
// --error: #882d2d;
// --whiteGrey: #f0f2f5;
// --whiteMoon: #f9f9f9;
// --whitePure: #fff;
// --orangeLight: #f5f1e8;

// --beige--secondary: #f6f2e4;
// --beige--primary: #fbf9f3;
// --white: white;
// --green--primary: #636f63;
// --sand--primary: #e0d2aa;
// --sand--10: #ede5cf;
// --green--secondary: #7d877f;
// --sand--50: #d0c099;
// --sand--30: #e7dcbd;
// --green--70: #a3a9a2;
// --green--50: #bcc0b9;
// --green--30: #d5d7d0;
// --green--10: #eeeee7;
// --graphite--70: #797979;
// --graphite--50: #a5a5a5;
// --graphite--30: #d2d2d2;
// --graphite--10: #ededed;
// --sand--secondary: #bfad87;
// --sand--90: #a39579;
// --sand--70: #c8b790;
// --blue--primary: #bec7d8;
// --blue--secondary: #8f9ab5;
// --red--primary: #612020;
// --red--secondary: #882d2d;
// --soft-green: #ecebe5;
// --soft-sand: #f6f1e4;
// --soft-blue: #efefee;

declare module "@mui/joy/styles" {
  interface Palette {
    action: {
      active: string;
    };
    grey: Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;
  }
  interface TypographySystem {
    pxToRem?: (px: number) => string;
  }
  interface CssVarsThemeOptions {
    shape?: {
      borderRadius?: number;
    };
    shadows?: string[];
    transitions?: {
      create: (
        props: string | string[],
        options?: { duration?: number; easing?: string; delay?: number }
      ) => string;
      duration?: {
        shortest?: number;
        standard?: number;
        complex?: number;
        enteringScreen?: number;
        leavingScreen?: number;
      };
      easing?: {
        easeInOut?: string;
        easeOut?: string;
        easeIn?: string;
        sharp?: string;
      };
      getAutoHeightDuration?: (height: number) => number;
    };
  }

  interface Theme {
    shape: {
      borderRadius: number;
    };
    shadows: string[];
    transitions: {
      create: (
        props: string | string[],
        options?: { duration?: number; easing?: string; delay?: number }
      ) => string;
      duration: {
        shortest: number;
        standard: number;
        complex: number;
        enteringScreen: number;
        leavingScreen: number;
      };
      easing: {
        easeInOut: string;
        easeOut: string;
        easeIn: string;
        sharp: string;
      };
      getAutoHeightDuration: (height: number) => number;
    };
  }
}

const getAutoHeightDuration = (height: number): number => {
  if (!height) {
    return 0;
  }
  const constant = height / 36;
  // ported from MUI’s createTransitions implementation
  return Math.round((4 + 15 * Math.pow(constant, 0.25) + constant / 5) * 10);
};

const defaultShape = {
  borderRadius: 4,
};

const defaultDurations = {
  shortest: 150,
  standard: 300,
  complex: 375,
  enteringScreen: 225,
  leavingScreen: 195,
};

const grey = {
  50: "#fafafa",
  100: "#f5f5f5",
  200: "#eeeeee",
  300: "#e0e0e0",
  400: "#bdbdbd",
  500: "#9e9e9e",
  600: "#757575",
  700: "#616161",
  800: "#424242",
  900: "#212121",
};

const defaultEasing = {
  easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
  easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
  easeIn: "cubic-bezier(0.4, 0, 1, 1)",
  sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
};

const defaultShadows = [
  "none",
  "0px 1px 3px rgba(0,0,0,0.2),0px 1px 1px rgba(0,0,0,0.14),0px 2px 1px rgba(0,0,0,0.12)",
  "0px 1px 5px rgba(0,0,0,0.2),0px 2px 2px rgba(0,0,0,0.14),0px 3px 1px rgba(0,0,0,0.12)",
  "0px 1px 8px rgba(0,0,0,0.2),0px 3px 4px rgba(0,0,0,0.14),0px 3px 3px rgba(0,0,0,0.12)",
  "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px rgba(0,0,0,0.14),0px 1px 10px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px rgba(0,0,0,0.14),0px 1px 14px rgba(0,0,0,0.12)",
  "0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px rgba(0,0,0,0.14),0px 1px 18px rgba(0,0,0,0.12)",
  "0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px rgba(0,0,0,0.14),0px 2px 16px rgba(0,0,0,0.12)",
  "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px rgba(0,0,0,0.14),0px 3px 14px rgba(0,0,0,0.12)",
  "0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px rgba(0,0,0,0.14),0px 3px 16px rgba(0,0,0,0.12)",
  "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px rgba(0,0,0,0.14),0px 4px 18px rgba(0,0,0,0.12)",
  "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px rgba(0,0,0,0.14),0px 4px 20px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px rgba(0,0,0,0.14),0px 5px 22px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px rgba(0,0,0,0.14),0px 5px 24px rgba(0,0,0,0.12)",
  "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px rgba(0,0,0,0.14),0px 5px 26px rgba(0,0,0,0.12)",
  "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px rgba(0,0,0,0.14),0px 6px 28px rgba(0,0,0,0.12)",
  "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px rgba(0,0,0,0.14),0px 6px 30px rgba(0,0,0,0.12)",
  "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px rgba(0,0,0,0.14),0px 6px 32px rgba(0,0,0,0.12)",
  "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px rgba(0,0,0,0.14),0px 7px 34px rgba(0,0,0,0.12)",
  "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px rgba(0,0,0,0.14),0px 7px 36px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px rgba(0,0,0,0.14),0px 8px 38px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px rgba(0,0,0,0.14),0px 8px 40px rgba(0,0,0,0.12)",
  "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px rgba(0,0,0,0.14),0px 8px 42px rgba(0,0,0,0.12)",
  "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px rgba(0,0,0,0.14),0px 9px 44px rgba(0,0,0,0.12)",
  "0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px rgba(0,0,0,0.14),0px 9px 46px rgba(0,0,0,0.12)",
];

const transitions = {
  duration: defaultDurations,
  easing: defaultEasing,
  create: (
    props: string | string[],
    options: { duration?: number; easing?: string; delay?: number } = {}
  ) => {
    const {
      duration = defaultDurations.standard,
      easing = defaultEasing.easeInOut,
      delay = 0,
    } = options;
    const propsArray = Array.isArray(props) ? props : [props];
    return propsArray.map((prop) => `${prop} ${duration}ms ${easing} ${delay}ms`).join(", ");
  },
  getAutoHeightDuration,
};

const theme = extendTheme({
  shape: defaultShape,
  shadows: defaultShadows,
  transitions,
  typography: {
    pxToRem: (px: number) => `${px / 16}rem`,
  },
  colorSchemes: {
    light: {
      palette: {
        primary: { 500: "#5e6f6e" },
        neutral: { 50: "#ffffff", 100: "#f4f1e9" },
        success: { 100: "#cef0e4" },
        warning: { 100: "#ffbc6d" },
        action: { active: "#5e6f6e" },
        grey,
      },
    },
  },
});

export default theme;
